import { supabase } from "@/integrations/supabase/client";

export interface CreditPackage {
  id: string;
  credits: number;
  price: number;
  bonus: number;
  popular: boolean;
}

export interface PricingSettings {
  costPer1k: string;
  packages: CreditPackage[];
}

/**
 * Fetch pricing settings from database
 */
export async function getPricingSettings(): Promise<PricingSettings> {
  const { data, error } = await supabase
    .from("pricing_settings" as any)
    .select("setting_key, setting_value");

  if (error) {
    console.error("Error fetching pricing settings:", error);
    // Return default values on error
    return {
      costPer1k: "190",
      packages: [
        { id: "1", credits: 500, price: 199, bonus: 0, popular: false },
        { id: "2", credits: 3000, price: 590, bonus: 0, popular: false },
        { id: "3", credits: 3400, price: 1000, bonus: 0, popular: true },
        { id: "4", credits: 5000, price: 1700, bonus: 0, popular: false },
        { id: "5", credits: 10000, price: 3300, bonus: 0, popular: false },
        { id: "6", credits: 100000, price: 30000, bonus: 0, popular: false },
      ],
    };
  }

  const settings: PricingSettings = {
    costPer1k: "190",
    packages: [],
  };

  (data as any[])?.forEach((row) => {
    if (row.setting_key === "cost_per_1k") {
      settings.costPer1k = row.setting_value as string;
    } else if (row.setting_key === "credit_packages") {
      settings.packages = row.setting_value as CreditPackage[];
    }
  });

  return settings;
}

/**
 * Update pricing settings in database
 */
export async function updatePricingSettings(
  settings: PricingSettings
): Promise<{ success: boolean; error?: string }> {
  try {
    // Update cost_per_1k
    const { error: costError } = await supabase
      .from("pricing_settings" as any)
      .update({ setting_value: settings.costPer1k } as any)
      .eq("setting_key", "cost_per_1k");

    if (costError) throw costError;

    // Update credit_packages
    const { error: packagesError } = await supabase
      .from("pricing_settings" as any)
      .update({ setting_value: settings.packages } as any)
      .eq("setting_key", "credit_packages");

    if (packagesError) throw packagesError;

    return { success: true };
  } catch (error) {
    console.error("Error updating pricing settings:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "Unknown error",
    };
  }
}
